import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, BedDouble, Users } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Container, Overline, Reveal } from "@/components/primitives";
import SelectedBadge from "@/components/SelectedBadge";

export default function PropertyDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/collection/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-8 text-center px-6">
        <p className="font-serif italic text-3xl text-ivory/70">This property is not part of the public Collection.</p>
        <Link to="/collection" className="text-gold text-xs tracking-[0.2em] uppercase">Return to the Collection</Link>
      </div>
    );
  }
  if (!p) return <div className="min-h-screen flex items-center justify-center text-ivory/40 text-sm tracking-widest uppercase">Loading…</div>;

  const gallery = (p.images || []).length ? p.images : [];
  const hero = p.hero_image ? fileUrl(p.hero_image) : IMAGES.amalfiVilla2;

  return (
    <div className="relative z-10" data-testid="property-detail">
      <section className="relative h-[80vh] min-h-[520px] overflow-hidden">
        <img src={hero} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/30 to-navy-900/50" />
        <Container className="relative h-full flex flex-col justify-end pb-16">
          <Link to="/collection" className="absolute top-28 left-6 md:left-10 inline-flex items-center gap-2 text-ivory/70 hover:text-gold text-xs tracking-[0.2em] uppercase transition-colors" data-testid="detail-back">
            <ArrowLeft size={15} /> The Collection
          </Link>
          <Reveal>
            <span className="text-[10px] tracking-[0.25em] uppercase text-gold">VillaFlow Selected Property</span>
            <h1 className="mt-4 font-serif font-light text-4xl md:text-7xl text-ivory tracking-tight">{p.name}</h1>
            <div className="mt-6 flex flex-wrap gap-8 text-ivory/70 text-sm">
              {p.location && <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-gold" />{p.location}</span>}
              {p.bedrooms ? <span className="inline-flex items-center gap-2"><BedDouble size={15} className="text-gold" />{p.bedrooms} Bedrooms</span> : null}
              {p.max_guests ? <span className="inline-flex items-center gap-2"><Users size={15} className="text-gold" />Up to {p.max_guests} Guests</span> : null}
            </div>
          </Reveal>
        </Container>
      </section>

      <Container className="py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>The Property</Overline>
              <p className="mt-8 font-serif text-2xl md:text-3xl text-ivory/90 font-light leading-snug">
                {p.description || "An exceptional villa selected for the VillaFlow Private Collection."}
              </p>
              <div className="gold-rule my-12 max-w-[120px]" />
              <p className="text-ivory/60 font-light leading-relaxed">
                This property has been individually reviewed and selected against the VillaFlow
                Selection Standard — assessed across property excellence, location and privacy,
                hospitality, guest experience, services and digital readiness.
              </p>
              {p.website_url && (
                <a href={p.website_url} target="_blank" rel="noreferrer" className="group mt-12 inline-flex items-center gap-3 bg-ivory hover:bg-gold text-navy px-10 py-4 text-xs tracking-[0.2em] uppercase transition-colors duration-500" data-testid="visit-property-btn">
                  Book / Visit Property <ArrowUpRight size={15} />
                </a>
              )}
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <Reveal delay={0.15}><SelectedBadge propertyName={p.name} size={280} /></Reveal>
          </div>
        </div>

        {gallery.length > 0 && (
          <div className="mt-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="property-gallery">
            {gallery.map((img, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={fileUrl(img)} alt={`${p.name} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
}
